import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { CategoryItem } from '@/components/CategoryItem';
import { CoverageBadge, CoverageStatus } from '@/components/CoverageBadge';
import { CoverageMobileRow } from '@/components/CoverageMobileRow';
import { Header } from '@/components/Header';
import { PageContainer } from '@/components/PageContainer';

import { categories } from '@/constants/categories';

import { useAuth } from '@/hooks/useAuth';

const coverageDetails = [
  {
    service: 'Flights',
    outpostPersonal: CoverageStatus.Covered,
    outpostGuest: CoverageStatus.Covered,
    summit: CoverageStatus.Covered,
  },
  {
    service: 'Food & Beverage',
    outpostPersonal: CoverageStatus.Covered,
    outpostGuest: CoverageStatus.Covered,
    summit: CoverageStatus.Sponsored,
  },
  {
    service: 'Activities',
    outpostPersonal: CoverageStatus.Covered,
    outpostGuest: CoverageStatus.Covered,
    summit: CoverageStatus.Sponsored,
  },
  {
    service: 'Accommodation',
    outpostPersonal: CoverageStatus.Sponsored,
    outpostGuest: CoverageStatus.Covered,
    summit: CoverageStatus.Sponsored,
  },
  {
    service: 'Travel Insurance',
    outpostPersonal: CoverageStatus.Covered,
    outpostGuest: CoverageStatus.Covered,
    summit: CoverageStatus.Covered,
  },
  {
    service: 'Local Transportation',
    outpostPersonal: CoverageStatus.Covered,
    outpostGuest: CoverageStatus.Covered,
    summit: CoverageStatus.Covered,
  },
  {
    service: 'Coworking',
    outpostPersonal: CoverageStatus.Covered,
    outpostGuest: CoverageStatus.Covered,
    summit: CoverageStatus.NotApplicable,
  },
];

function UnleashCategories() {
  const { user, signOut } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState<number | null>(null);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setIsVisible(true);
  }, []);

  const toggleCategory = (categoryId: number) => {
    setExpandedCategoryId(expandedCategoryId === categoryId ? null : categoryId);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onSignOut={signOut} />
      <PageContainer>
        <header className="mb-8">
          <h1
            className={`mb-8 text-center text-3xl font-bold text-gray-900 transition-all duration-700 ${
              isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
            }`}
          >
            Unleash Categories
          </h1>
        </header>

        {/* Blurb Section - Red Background */}
        <div
          className={`mb-8 rounded-lg bg-blush-200 p-6 transition-all delay-150 duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <p className="mb-2 text-lg font-bold text-red-600">Unleash fuels your growth.</p>
          <p className="text-gray-700">
            It&apos;s your chance to invest in what makes you feel alive — from creativity and
            learning to wellness and adventure. Explore the categories below for inspiration, and
            check out the{' '}
            <Link to="/faq" className="text-red-600 underline hover:text-red-700">
              Unleash FAQ
            </Link>{' '}
            for claim guidelines.
          </p>
        </div>

        {/* Categories List */}
        <div className="divide-y divide-gray-200">
          {categories
            .filter((category) => category.userSelectable !== false)
            .map((category, index) => (
              <CategoryItem
                key={category.id}
                category={category}
                index={index}
                isVisible={isVisible}
                isExpanded={expandedCategoryId === category.id}
                onToggle={() => toggleCategory(category.id)}
              >
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
                  <div className="border-b border-gray-200 bg-linear-to-r from-blush-200 to-blush-100 px-4 py-3 md:px-6 md:py-4">
                    <h3 className="text-base font-bold text-gray-900 md:text-lg">
                      Coverage Details
                    </h3>
                    <p className="mt-1 text-xs text-gray-600 md:text-sm">
                      What&apos;s covered for X-Outpost and X-Summit events
                    </p>
                  </div>
                  {/* Desktop Table View */}
                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-300 bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 lg:px-6 lg:py-4 lg:text-sm">
                            Service
                          </th>
                          <th
                            colSpan={2}
                            className="border-l-2 border-gray-300 px-4 py-3 text-center text-xs font-bold text-gray-900 lg:px-6 lg:py-4 lg:text-sm"
                          >
                            X-OUTPOST
                          </th>
                          <th className="border-l-2 border-gray-300 px-4 py-3 text-center text-xs font-bold text-gray-900 lg:px-6 lg:py-4 lg:text-sm">
                            X-SUMMIT
                          </th>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="px-4 py-2 lg:px-6 lg:py-3">
                            <span className="sr-only">Service</span>
                          </th>
                          <th className="border-l border-gray-200 bg-blush-50 px-4 py-2 text-center text-xs font-semibold text-gray-700 lg:px-6 lg:py-3">
                            PERSONAL
                          </th>
                          <th className="border-l border-gray-200 bg-blush-50 px-4 py-2 text-center text-xs font-semibold text-gray-700 lg:px-6 lg:py-3">
                            GUEST(S)
                          </th>
                          <th className="border-l border-gray-200 bg-blush-50 px-4 py-2 text-center text-xs font-semibold text-gray-700 lg:px-6 lg:py-3">
                            PERSONAL
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {coverageDetails.map((row, idx) => (
                          <tr
                            key={row.service}
                            className={`transition-colors ${
                              idx % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-4 py-3 text-xs font-semibold text-gray-900 lg:px-6 lg:py-4 lg:text-sm">
                              {row.service}
                            </td>
                            <td className="border-l border-gray-200 px-4 py-3 text-center lg:px-6 lg:py-4">
                              <CoverageBadge value={row.outpostPersonal} />
                            </td>
                            <td className="border-l border-gray-200 px-4 py-3 text-center lg:px-6 lg:py-4">
                              <CoverageBadge value={row.outpostGuest} />
                            </td>
                            <td className="border-l border-gray-200 px-4 py-3 text-center lg:px-6 lg:py-4">
                              <CoverageBadge value={row.summit} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="divide-y divide-gray-200 md:hidden">
                    {coverageDetails.map((row, idx) => (
                      <CoverageMobileRow key={row.service} row={row} isAlternate={idx % 2 === 1} />
                    ))}
                  </div>
                </div>
              </CategoryItem>
            ))}
        </div>
      </PageContainer>
    </div>
  );
}

export { UnleashCategories };
